import { atom, atomFamily, selectorFamily } from 'recoil'
import BigNumber from 'bignumber.js'
import { markPriceMapState, marketInfoValue } from '@/recoil/Market'
import { orderBookState } from '@/recoil/OrderBook'
import { accountWalletState } from '@/recoil/Wallet'
import {
  positionInfoState,
  positionDetailValue,
  collateralInfoValue
} from '@/recoil/Position'
import { accountInfoState } from '@/recoil/AccountInfo'
import { ORDER_TYPE_MAP, ORDER_SIDE, ORDER_INPUT_MAP } from '@/constants/order'
import { isValidNumber } from '@/utils/numberFormat'
import { getErrorMessage } from '@/utils/order'
import { calculateMaxOrderSize } from '@/utils/futures'

function generateDefaultOrderState() {
  return {
    side: ORDER_SIDE.buy,
    price: '',
    size: '',
    total: '',
    estimatedValue: ''
  }
}

export const showCancelAllConfirmationPopupState = atom({
  key: 'ShowCancelAllConfirmationPopup',
  default: false
})

export const orderTypeState = atom({
  key: 'OrderType',
  default: ORDER_TYPE_MAP.limit
})

export const _orderFormStateFamily = atomFamily({
  key: '_OrderForm',
  default: generateDefaultOrderState()
})

export const orderFormStateFamily = selectorFamily({
  key: 'OrderForm',
  get:
    id =>
    ({ get }) =>
      get(_orderFormStateFamily(id)),
  set:
    id =>
    (
      { get, set, reset },
      { key, value, resetConfig = {}, isReset = false, isBestPriceOrder }
    ) => {
      const orderFormState = _orderFormStateFamily(id)
      if (isReset) {
        return set(orderFormState, {
          ...get(orderFormState),
          price: '',
          size: '',
          total: '',
          estimatedValue: '',
          ...resetConfig
        })
      }

      if (key === 'type') {
        const data = generateDefaultOrderState()
        data.side = get(orderFormState).side
        return set(orderFormState, data)
      }

      if (key === 'side') {
        return set(orderFormState, {
          ...get(orderFormState),
          [key]: value
        })
      }

      value = String(value).replaceAll(',', '')
      if (!isValidNumber(value)) {
        return
      }

      const orderType = get(orderTypeState)
      const data = {
        ...get(orderFormState),
        [key]: value,
        estimatedValue: ''
      }

      if (key === ORDER_INPUT_MAP.price) {
        if (orderType === ORDER_TYPE_MAP.market) {
          return
        }

        if (value === '') {
          data.total = ''
        } else if (data.size !== '') {
          data.total = BigNumber(value).times(data.size).toFixed()
        } else if (data.total !== '') {
          const { sizeDecimal } = get(marketInfoValue)
          const result = BigNumber(data.total).div(data.price)
          data.size = result.isFinite()
            ? result.dp(sizeDecimal, BigNumber.ROUND_DOWN).toFixed()
            : ''
        }
        return set(orderFormState, data)
      }

      if (key === ORDER_INPUT_MAP.size) {
        if (value === '') {
          data.total = ''
        } else if (data.price !== '') {
          data.total = BigNumber(data.price).times(value).toFixed()
        } else if (orderType === ORDER_TYPE_MAP.market) {
          let total = BigNumber(0)
          const { bids, asks } = get(orderBookState)
          const quoteList = data.side === ORDER_SIDE.buy ? asks : bids
          if (quoteList.length === 0) {
            const { symbol } = get(marketInfoValue)
            const markPrice = get(markPriceMapState)[symbol] || 0
            total = BigNumber(markPrice).times(value)
          } else if (isBestPriceOrder) {
            const quotePrice = quoteList[0] ? quoteList[0].price : 0
            total = BigNumber(quotePrice).times(value)
          } else {
            for (
              let i = 0, amount = BigNumber(value);
              i < quoteList.length && amount.gt(0);
              i++
            ) {
              const { price, size } = quoteList[i]
              const currentPriceSize = amount > size ? size : amount
              total = total.plus(BigNumber(price).times(currentPriceSize))
              amount = amount.minus(currentPriceSize)
            }
          }
          data.total = total.toFixed()
          data.estimatedValue = ORDER_INPUT_MAP.total
        } else if (data.total !== '') {
          const { priceDecimal } = get(marketInfoValue)
          const result = BigNumber(data.total).div(value)
          data.price = result.isFinite()
            ? result.dp(priceDecimal, BigNumber.ROUND_DOWN).toFixed()
            : ''
        }
        return set(orderFormState, data)
      }

      if (key === ORDER_INPUT_MAP.total) {
        if (value !== '') {
          if (data.price !== '') {
            const { sizeDecimal } = get(marketInfoValue)
            const result = BigNumber(value).div(data.price)
            data.size = result.isFinite()
              ? result.dp(sizeDecimal, BigNumber.ROUND_DOWN).toFixed()
              : ''
          } else {
            if (orderType === ORDER_TYPE_MAP.market) {
              const { bids, asks } = get(orderBookState)
              const quoteList = data.side === ORDER_SIDE.buy ? asks : bids
              const { sizeDecimal } = get(marketInfoValue)
              let orderSize = BigNumber(0)
              if (quoteList.length === 0) {
                const { symbol } = get(marketInfoValue)
                const markPrice = get(markPriceMapState)[symbol] || 1
                orderSize = BigNumber(value).div(markPrice)
              } else if (isBestPriceOrder) {
                const quotePrice = quoteList[0] ? quoteList[0].price : 1
                orderSize = BigNumber(value).div(quotePrice)
              } else {
                for (
                  let i = 0, amount = BigNumber(value);
                  i < quoteList.length && amount.gt(0);
                  i++
                ) {
                  const { price, size } = quoteList[i]
                  const currentTotalCost = BigNumber(price).times(size)
                  const totalSize = currentTotalCost.gt(amount)
                    ? BigNumber(amount).div(price)
                    : size

                  // Amount too small to buy minimum size
                  if (
                    BigNumber(totalSize)
                      .dp(sizeDecimal, BigNumber.ROUND_DOWN)
                      .eq(0)
                  ) {
                    break
                  }

                  orderSize = orderSize.plus(totalSize)
                  amount = amount.minus(BigNumber(price).times(totalSize))
                }
              }
              data.estimatedValue = ORDER_INPUT_MAP.size
              data.size = orderSize
                .dp(sizeDecimal, BigNumber.ROUND_DOWN)
                .toFixed()
            } else if (data.size !== '') {
              const { priceDecimal } = get(marketInfoValue)
              const result = BigNumber(value).div(data.size)
              data.price = result.isFinite()
                ? result.dp(priceDecimal, BigNumber.ROUND_DOWN).toFixed()
                : ''
            }
          }
        } else {
          if (data.price !== '' || orderType === ORDER_TYPE_MAP.market) {
            data.size = ''
          } else if (data.size !== '') {
            data.price = ''
          }
        }
        return set(orderFormState, data)
      }
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const orderFormIsReduceOnlyOrderState = atom({
  key: 'OrderFormIsReduceOnlyOrder',
  default: false
})

export const maxOrderSizeValue = selectorFamily({
  key: 'MaxOrderSize',
  get:
    id =>
    ({ get }) => {
      const markPriceMap = get(markPriceMapState)
      const marketInfo = get(marketInfoValue)
      const positionInfo = get(positionInfoState)
      const accountInfo = get(accountInfoState)
      const isReduceOrder = get(orderFormIsReduceOnlyOrderState)
      const markPrice = markPriceMap[marketInfo.symbol]

      if (!markPrice || !marketInfo.symbol || !accountInfo.account_id) {
        return 0
      }

      const orderForm = get(_orderFormStateFamily(id))
      const { totalCollateral } = get(collateralInfoValue)
      const { initialMarginMap, totalInitialMargin } = get(positionDetailValue)
      const positionList = positionInfo.rows || []
      const position =
        positionList.find(({ symbol }) => symbol === marketInfo.symbol) || {}

      const {
        position_qty: positionQty = 0,
        pending_long_qty: pendingLongQty,
        pending_short_qty: pendingShortQty
      } = position

      if (isReduceOrder && !BigNumber(positionQty).eq(0)) {
        if (
          (BigNumber(positionQty).gt(0) && orderForm.side !== ORDER_SIDE.buy) ||
          (BigNumber(positionQty).lt(0) && orderForm.side !== ORDER_SIDE.sell)
        ) {
          return BigNumber(positionQty).abs().toNumber()
        }
        return 0
      }

      return calculateMaxOrderSize({
        markPrice,
        orderSide: orderForm.side,
        positionQty,
        pendingLongQty,
        pendingShortQty,
        accountMaxLeverage: accountInfo.max_leverage,
        totalCollateral,
        totalInitialMargin,
        otherPositionTotalInitialMargin: BigNumber(totalInitialMargin)
          .minus(initialMarginMap[marketInfo.symbol] || 0)
          .toNumber()
      })
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const orderPriceErrorValue = selectorFamily({
  key: 'OrderPriceError',
  get:
    id =>
    ({ get }) => {
      const value = get(_orderFormStateFamily(id)).price
      if (value === '') {
        return ''
      }
      const { quote_min, quote_max, quote_tick } = get(marketInfoValue)
      return getErrorMessage(ORDER_INPUT_MAP.price, value, {
        min: quote_min,
        max: quote_max,
        tick: quote_tick
      })
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const orderSizeErrorValue = selectorFamily({
  key: 'OrderSizeError',
  get:
    id =>
    ({ get }) => {
      const orderType = get(orderTypeState)
      const { side, price, size, estimatedValue } = get(
        _orderFormStateFamily(id)
      )
      if (size === '') {
        return ''
      }
      const {
        baseCurrency,
        sizeDecimal,
        base_min,
        base_max,
        base_tick,
        min_notional,
        isFutures
      } = get(marketInfoValue)
      const maxOrderSize = get(maxOrderSizeValue(id))
      const minSizeValue = BigNumber(min_notional)
        .div(price)
        .dp(sizeDecimal, BigNumber.ROUND_UP)
      const rules = {
        min:
          price !== '' && !isFutures
            ? BigNumber.max(base_min, minSizeValue).toNumber()
            : base_min,
        max: isFutures
          ? BigNumber.min(base_max, maxOrderSize)
              .dp(sizeDecimal, BigNumber.ROUND_DOWN)
              .toNumber()
          : base_max,
        tick: base_tick
      }

      if (!isFutures) {
        const balance = get(accountWalletState)[baseCurrency]?.available || 0
        if (
          (orderType === ORDER_TYPE_MAP.limit && side === ORDER_SIDE.sell) ||
          (orderType === ORDER_TYPE_MAP.market &&
            side === ORDER_SIDE.sell &&
            estimatedValue !== ORDER_INPUT_MAP.size) ||
          (orderType === ORDER_TYPE_MAP.market &&
            side === ORDER_SIDE.sell &&
            estimatedValue === ORDER_INPUT_MAP.size &&
            balance === 0)
        ) {
          rules.balance = balance
        }
      }
      return getErrorMessage(ORDER_INPUT_MAP.size, size, rules)
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const orderTotalErrorValue = selectorFamily({
  key: 'OrderTotalError',
  get:
    id =>
    ({ get }) => {
      const orderType = get(orderTypeState)
      const { side, total, estimatedValue } = get(_orderFormStateFamily(id))
      const { quoteCurrency, min_notional, isFutures } = get(marketInfoValue)
      if (total === '') {
        return ''
      }

      const rules = {
        min: min_notional
      }
      if (!isFutures) {
        const balance = get(accountWalletState)[quoteCurrency]?.available || 0
        if (
          (orderType === ORDER_TYPE_MAP.limit && side === ORDER_SIDE.buy) ||
          (orderType === ORDER_TYPE_MAP.market &&
            side === ORDER_SIDE.buy &&
            estimatedValue !== ORDER_INPUT_MAP.total) ||
          (orderType === ORDER_TYPE_MAP.market &&
            side === ORDER_SIDE.buy &&
            estimatedValue === ORDER_INPUT_MAP.total &&
            balance === 0)
        ) {
          rules.balance = balance
        }
      }
      return getErrorMessage(ORDER_INPUT_MAP.total, total, rules)
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const orderFormAutoFillQueueState = atom({
  key: 'OrderFormAutoFillQueue',
  default: []
})

export const orderFormLimitOrderOptionState = atom({
  key: 'OrderFormLimitOrderOption',
  default: null
})

export const orderFormIsHiddenOrderState = atom({
  key: 'OrderFormIsHiddenOrder',
  default: false
})

export const orderFormIsBestPriceOrderState = atom({
  key: 'OrderFormIsBestPriceOrder',
  default: false
})
